import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/brand/Logo';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useAuth } from '../hooks/useAuth';
import { Cpu, Brain, Sparkles } from 'lucide-react';
import DotField from '../components/DotField';

export const EarlyAccess: React.FC = () => {
    const [accessCode, setAccessCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (isAuthenticated) {
            navigate('/dashboard', { replace: true });
        }
    }, [isAuthenticated, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!accessCode.trim()) {
            setError('Please enter an early access code');
            return;
        }

        setIsLoading(true);

        try {
            const { validateCode } = await import('../api/endpoints/auth');
            await validateCode(accessCode.trim());
            sessionStorage.setItem('accessCode', accessCode.trim());
            navigate(`/signup`);
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Invalid access code. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#f8fafc] dark:bg-background">
      {/* LEFT COLUMN: Branding & Visuals (lg screens only) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-grey-50 dark:bg-grey-900/40 text-grey-900 dark:text-white flex-col justify-between p-16 border-r border-grey-200 dark:border-grey-800/60">
        {/* DotField Background */}
        <div className="absolute inset-0 z-0 pointer-events-none opacity-100">
          <DotField
            dotRadius={1.6}
            dotSpacing={20}
            cursorRadius={250}
            cursorForce={0.06}
            glowRadius={0}
            gradientFrom="rgba(56, 80, 160, 0.85)"
            gradientTo="rgba(28, 40, 80, 0.75)"
            glowColor="transparent"
          />
        </div>
        {/* Top Header */}
        <div className="relative z-10 flex items-center">
          <Logo variant="full" size="2xl" />
        </div>

        {/* Center Visuals & Feature highlights */}
        <div className="relative z-10 my-auto flex flex-col gap-10 max-w-md">
          {/* Header Typography */}
          <div className="flex flex-col gap-3 ml-5">
            <h2 className="text-3xl font-bold text-grey-900 dark:text-white tracking-tight leading-tight">
              Quantum ML & Orchestration Platform.
            </h2>
            <p className="text-grey-600 dark:text-grey-400 text-sm mt-1 leading-relaxed">
              QuantumCue is a no-code, end-to-end quantum orchestration platform
              for building, training, benchmarking, and deploying quantum
              machine learning models across leading quantum providers.
            </p>
          </div>

          {/* Minimalist Feature Highlight List */}
          <div className="flex flex-col gap-4 mt-4 ml-5">
            <div className="bg-white/90 dark:bg-grey-900/90 backdrop-blur-sm rounded-xl p-4 border border-grey-200/60 dark:border-grey-800/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] flex items-start gap-4">
              <div className="text-[#3850A0] dark:text-brand-300 flex-shrink-0 mt-1">
                <Cpu size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-grey-800 dark:text-grey-100">
                  Unified Gateway
                </h3>
                <p className="text-xs text-grey-500 dark:text-grey-400 mt-0.5 leading-relaxed">
                  Deploy workflows seamlessly across top hardware providers.
                </p>
              </div>
            </div>

            <div className="bg-white/90 dark:bg-grey-900/90 backdrop-blur-sm rounded-xl p-4 border border-grey-200/60 dark:border-grey-800/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] flex items-start gap-4">
              <div className="text-[#3850A0] dark:text-brand-300 flex-shrink-0 mt-1">
                <Brain size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-grey-800 dark:text-grey-100">
                  AI-Guided Orchestration
                </h3>
                <p className="text-xs text-grey-500 dark:text-grey-400 mt-0.5 leading-relaxed">
                  Interact with your Copilot to optimize your quantum trained
                  machine learning models instantly.
                </p>
              </div>
            </div>

            <div className="bg-white/90 dark:bg-grey-900/90 backdrop-blur-sm rounded-xl p-4 border border-grey-200/60 dark:border-grey-800/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] flex items-start gap-4">
              <div className="text-[#3850A0] dark:text-brand-300 flex-shrink-0 mt-1">
                <Sparkles size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-grey-800 dark:text-grey-100">
                  Zero-Code Curation
                </h3>
                <p className="text-xs text-grey-500 dark:text-grey-400 mt-0.5 leading-relaxed">
                  Upload, clean, and auto-label datasets without a single line
                  of code.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom brand footer spacing */}
        <div className="relative z-10 text-xs text-grey-400 dark:text-grey-500 ml-5 mt-5">
          © {new Date().getFullYear()} QuantumCue. All rights reserved.
        </div>
      </div>

      {/* RIGHT COLUMN: Form & Sign-Up */}
      <div className="flex-1 flex flex-col justify-center items-center p-6 sm:p-12 md:p-16 lg:w-1/2 bg-white dark:bg-grey-950 min-h-screen">
        <div className="w-full max-w-[400px] flex flex-col justify-between h-full min-h-[500px]">
          <div className="h-6" />

          <div className="my-auto w-full">
            {/* Header text */}
            <div className="mb-8 text-center lg:text-left">
              <h1 className="text-2xl font-bold text-grey-900 dark:text-white tracking-tight">
                Invite Code
              </h1>
              <p className="text-sm text-grey-500 dark:text-grey-400 mt-2">
                Enter your access code to enter the platform.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700 text-center font-medium">{error}</p>
                </div>
              )}

              <div className="space-y-2">
                <Input
                  label="Access Code"
                  type="text"
                  name="accessCode"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  placeholder="Enter invite code"
                  autoComplete="off"
                  disabled={isLoading}
                  className="text-center text-lg tracking-widest font-bold"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                isLoading={isLoading}
                variant="luxury"
              >
                {isLoading ? 'Verifying...' : 'Sign Up'}
              </Button>
            </form>

            <div className="mt-6 text-center lg:text-left">
              <p className="text-sm text-grey-500 dark:text-grey-400">
                Already have an account?{' '}
                <a
                  href="/login"
                  className="font-semibold hover:underline transition-colors"
                  style={{ color: "#3850A0" }}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/login');
                  }}
                >
                  Sign in
                </a>
              </p>
            </div>
          </div>

          <div className="h-6" />
        </div>
      </div>
    </div>
  );
};

export default EarlyAccess;
